import { useState } from "react";

import { ErrorMessage } from "@/app/components/error-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { PublicSession } from "@classview/shared-types";

import { getSessionQueryKey } from "../api/get-session";
import { useUpdateRecurringSession } from "../api/update-session";
import { AddSessionFormData } from "../types/add-session-schema";
import { transformRecurringFormDataToApiData } from "../utils/session-data-transformation";

type UpdateRecurringSessionModalProps = {
  sessionId: string;
  recurringId: string;
  isOpen: boolean;
  data: AddSessionFormData | undefined;
  onClose: () => void;
};

export function UpdateRecurringSessionModal({
  sessionId,
  recurringId,
  isOpen,
  data,
  onClose,
}: UpdateRecurringSessionModalProps) {
  const { organisation } = useOrganisation();
  const [isUpdatingAllSessions, setIsUpdatingAllSessions] = useState(false);
  const {
    mutateAsync: updateRecurringSession,
    isError: updateRecurringSessionIsError,
    error,
    isPending: updateRecurringSessionIsPending,
  } = useUpdateRecurringSession({
    onSuccess: async (sessions) => {
      const session = sessions.find((session) => session.id === sessionId);
      if (!session) {
        return;
      }
      //   form.reset(transformApiDataToFormData(session));
      queryClient.setQueryData<PublicSession>(
        getSessionQueryKey({
          orgId: organisation.id,
          sessionId: session.id,
        }),
        session,
      );
    },
  });

  async function handleUpdateAllSessions() {
    if (!data) {
      return;
    }

    setIsUpdatingAllSessions(true);
    await updateRecurringSession({
      id: sessionId,
      session: transformRecurringFormDataToApiData({
        data,
        recurringId,
        shouldUpdateAllSessions: true,
      }),
    });
    onClose();
  }

  async function handleUpdateThisSession() {
    if (!data) {
      return;
    }

    setIsUpdatingAllSessions(false);
    await updateRecurringSession({
      id: sessionId,
      session: transformRecurringFormDataToApiData({
        data,
        recurringId,
      }),
    });
    onClose();
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Update Recurring Session</DialogTitle>
          <DialogDescription>
            You are trying to update a recurring session.
          </DialogDescription>
        </DialogHeader>
        {updateRecurringSessionIsError && <ErrorMessage error={error} />}
        <DialogFooter>
          <Button
            type="submit"
            variant="outline"
            className="mr-auto"
            onClick={handleUpdateAllSessions}
            disabled={updateRecurringSessionIsPending}
            isLoading={updateRecurringSessionIsPending && isUpdatingAllSessions}
            isAnimated
          >
            Update All Sessions
          </Button>

          <Button
            type="submit"
            variant="default"
            onClick={handleUpdateThisSession}
            disabled={updateRecurringSessionIsPending}
            isLoading={
              updateRecurringSessionIsPending && !isUpdatingAllSessions
            }
            isAnimated
          >
            Update this session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
